import { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Calendar, MapPin, Sparkles, ArrowRight, ChevronRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DigestLandingPage — the page email recipients land on when they tap
 * a link inside their daily "New events" digest email.
 *
 * Shows *only* the events that were in this specific digest, with a
 * soft login CTA below to unlock the full events feed. This keeps
 * public event pages fully public (great for SEO / Google indexing /
 * WhatsApp shares) while creating a conversion moment at the email
 * touchpoint.
 */
const DigestLandingPage = () => {
  const { digestId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const highlightedEventId = useMemo(() => searchParams.get('event'), [searchParams]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/digests/${digestId}`);
        setDigest(data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Digest not found' : 'Failed to load digest');
      } finally {
        setLoading(false);
      }
    })();
  }, [digestId]);

  // Auto-scroll to the specific event when arriving via a card link.
  useEffect(() => {
    if (!highlightedEventId || loading) return;
    const el = document.getElementById(`ev-${highlightedEventId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightedEventId, loading]);

  const formatDate = (raw) => {
    if (!raw) return 'TBC';
    try {
      const d = new Date(raw);
      return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch {
      return raw;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-fuchsia-400" />
      </div>
    );
  }

  if (error || !digest) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-sm w-full bg-card border-border/50 rounded-3xl">
          <CardContent className="p-8 text-center">
            <p className="text-lg font-semibold mb-2">Digest not found</p>
            <p className="text-sm text-muted-foreground mb-6">
              This digest link may have expired. Head to the events feed to see what&apos;s on.
            </p>
            <Button onClick={() => navigate('/events')} className="rounded-full" data-testid="digest-error-goto-events">
              Browse all events
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { first_name, events = [], total_events = 0 } = digest;

  return (
    <div className="min-h-screen bg-background" data-testid="digest-landing-page">
      {/* Hero header */}
      <div
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg,#9333ea 0%,#db2777 50%,#e040fb 100%)',
        }}
      >
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.15), transparent 60%)',
        }} />
        <div className="relative max-w-2xl mx-auto px-5 pt-10 pb-12 text-center">
          <img
            src="/rollm8s-logo-real.png"
            alt="RollM8s"
            className="w-16 h-16 mx-auto mb-3 rounded-2xl shadow-lg"
          />
          <p className="text-white/90 text-xs font-bold tracking-[0.2em] uppercase mb-3">
            Your daily skate digest
          </p>
          <h1 className="text-3xl sm:text-4xl font-black leading-tight mb-2">
            <span className="text-white">{total_events} new event{total_events !== 1 ? 's' : ''}</span>
          </h1>
          <p className="text-white/90 text-base font-medium">
            for you, {first_name} 🛼
          </p>
        </div>
      </div>

      {/* Events */}
      <div className="max-w-2xl mx-auto px-4 -mt-6 pb-4 relative z-10">
        {events.length === 0 ? (
          <Card className="rounded-2xl bg-card border-border/50">
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground text-sm">
                The events in this digest are no longer available.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {events.map((ev) => {
              const thumb = ev.thumbnail || ev.image_url || ev.flyer_url;
              const venue = ev.venue || ev.location?.name;
              const isHighlighted = highlightedEventId === ev.event_id;
              return (
                <Link
                  key={ev.event_id}
                  id={`ev-${ev.event_id}`}
                  to={`/events/${ev.event_id}`}
                  data-testid={`digest-event-${ev.event_id}`}
                  className={`block group ${isHighlighted ? 'ring-2 ring-fuchsia-400 rounded-2xl' : ''}`}
                >
                  <Card className="rounded-2xl bg-card border-border/50 hover:border-fuchsia-400/60 transition-all overflow-hidden">
                    <CardContent className="p-3 flex gap-3 items-stretch">
                      <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-fuchsia-500 to-purple-600">
                        {thumb && (
                          <img
                            src={thumb}
                            alt={ev.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 py-1">
                        <p className="font-bold text-base leading-tight mb-1 line-clamp-2 group-hover:text-fuchsia-400 transition-colors">
                          {ev.name}
                        </p>
                        <div className="flex items-center gap-1.5 text-xs text-fuchsia-400 font-semibold mb-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(ev.date)}{ev.time ? ` · ${ev.time}` : ''}
                        </div>
                        {venue && (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground line-clamp-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            {venue}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center pr-1">
                        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-fuchsia-400 transition-colors" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}

        {/* Convert to full events feed */}
        <Card className="mt-6 rounded-3xl border-fuchsia-400/40 overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(147,51,234,0.12) 0%, rgba(219,39,119,0.08) 100%)',
          }}
          data-testid="digest-convert-card"
        >
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-fuchsia-400/15 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-fuchsia-400" />
            </div>
            {user ? (
              <>
                <h2 className="text-lg font-bold mb-1">See what else is popping</h2>
                <p className="text-sm text-muted-foreground mb-5">
                  Explore every skate event happening near you — jams, socials, rollerdiscos and more.
                </p>
                <Button
                  onClick={() => navigate('/events')}
                  className="rounded-full h-11 px-6 bg-fuchsia-400 hover:bg-fuchsia-500 text-black font-bold"
                  data-testid="digest-see-all-events-btn"
                >
                  See all skate events <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </>
            ) : (
              <>
                <h2 className="text-lg font-bold mb-1">Want to see all skate events?</h2>
                <p className="text-sm text-muted-foreground mb-5">
                  Log in to unlock the full events feed, RSVP, and connect with the community near you.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
                  <Button
                    onClick={() => navigate(`/login?redirect=/events`)}
                    className="rounded-full h-11 px-6 bg-fuchsia-400 hover:bg-fuchsia-500 text-black font-bold"
                    data-testid="digest-login-btn"
                  >
                    Log in <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                  <Button
                    onClick={() => navigate(`/register?redirect=/events`)}
                    variant="outline"
                    className="rounded-full h-11 px-6"
                    data-testid="digest-signup-btn"
                  >
                    Create an account
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Footer nudge */}
        <p className="text-center text-xs text-muted-foreground/70 mt-6 mb-8">
          Not into the daily digest?{' '}
          <Link to="/my-profile?tab=notifications" className="text-fuchsia-400 underline">
            Change how often you hear from us
          </Link>
        </p>
      </div>
    </div>
  );
};

export default DigestLandingPage;
